import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import LoginIcon from '@mui/icons-material/Login';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import * as yup from 'yup';
import { useFormik } from 'formik';
import "./SignIn.scss";
import { UcThemeContext } from '../../lib/UcThemeProvider';
import { useExternalLoginProviders } from '../auth/useExternalLoginProviders'
import { ApiClient } from '../../lib/ApiClient';
import { UrlHelpers } from '../../lib/Helpers';
import { useAuthenticated } from '../auth/useAuthenticated'
import { useLogin } from './AccountEndpoints';
import { getUrl, useDomainNavigate, domain } from '../../domain';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { ProviderIcon } from '../common/ProviderIcon';

const apiClient = new ApiClient();

export default function SignIn() {
    let navigate = useDomainNavigate();
    const params = new URLSearchParams(window.location.search);
    let returnUrlParam = getUrl(decodeURIComponent(UrlHelpers.getSafeUrl(params.get('ReturnUrl') || "", "/account/logout")));
    let emailParam = decodeURI(params.get('email'));
    const loginContext = useLogin(returnUrlParam);
    const [externalSuccessMessage, setExternalSuccessMessage] = React.useState(new URLSearchParams(window.location.search).get('SuccessMessage'));
    const [externalErrorMessage, setExternalErrorMessage] = React.useState(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const errorMessage = searchParams.get('ErrorMessage');
        if (errorMessage) return errorMessage;

        const errorCode = searchParams.get('error');
        const errorMessages = {
            'provider_unavailable': 'The login provider is no longer available. Please try again or choose a different login method.',
            'remote_failure': 'The login provider returned an error. Please try again or contact your administrator.',
        };
        return errorMessages[errorCode] || null;
    });
    const userAuth = useAuthenticated(apiClient);
    const externalLoginProviders = useExternalLoginProviders(apiClient);
    // eslint-disable-next-line no-unused-vars
    const [themeState, themeDispatch] = React.useContext(UcThemeContext);
    const [showFormError, setShowFormError] = React.useState(false);
    const [errorCode, setErrorCode] = React.useState(null);
    const [errorMessage, setErrorMessage] = React.useState(null); // default to the other error
    const [showPassword, setShowPassword] = React.useState(false);
    const theme = useTheme();

    if (!userAuth.isAuthenticating && userAuth.isAuthenticated) {
        window.location = returnUrlParam;
    }

    const axiosconfig = {
        headers: {
            "Content-Type": "application/json",
            "SubDomain": domain.subDomain
        },
    };

    if (emailParam === 'null') {
        emailParam = null;
    }

    const initialValues = {
        email: emailParam !== null ? emailParam : '',
        password: '',
        rememberMe: false
    };
    const validationSchema = yup.object({
        email: yup
            .string('Enter your email')
            .email('Enter a valid email')
            .required('Email is required'),
        password: yup
            .string('Enter your password')
            .min(6, 'Password should be of minimum 6 characters length')
            .required('Password is required'),
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: (values, actions) => {
            setShowFormError(false);
            setErrorCode(null);
            setErrorMessage(null);
            setExternalErrorMessage(null);
            setExternalSuccessMessage(null);
            axios.post(getUrl(`/identity/account/login`), JSON.stringify(values), axiosconfig)
                .then(response => {
                    formik.setSubmitting(false);

                    if (response.status === 200) {
                        window.location = returnUrlParam;
                    }
                })
                .catch(error => {
                    formik.setSubmitting(false);
                    setShowFormError(true);

                    if (error.response && error.response.data) {
                        if (error.response.status === 303) {
                            navigate(`/account/loginWith2fa?rememberMe=${formik.values.rememberMe}&ReturnUrl=${encodeURIComponent(returnUrlParam)}`);
                        }

                        setErrorCode(error.response.data.errorCode);
                        if (error.response.data.errorMessage) {
                            setErrorMessage(error.response.data.errorMessage);
                        }
                        else {
                            setErrorMessage("An error occurred.");
                        }
                    }
                    else
                    {
                        setErrorMessage("The email or password is incorrect");
                    }
                });
        },
    });

    const resendEmailConfirmation = () => {
        formik.setSubmitting(true);
        // Need to encode return url
        const returnUrlEncoded = encodeURIComponent(returnUrlParam);
        axios.post(getUrl(`/identity/account/resend/email/confirmation?ReturnUrl=${returnUrlEncoded}`), JSON.stringify({
            email: formik.values.email,
            password: formik.values.password,
        }), axiosconfig)
            .then(response => { // response contains user Object
                formik.setSubmitting(false);

                if (response.status === 200) {
                    setShowFormError(false);
                    // setShowEmailConfirmedError(false);
                    window.location = returnUrlParam;
                }
            })
            .catch(error => {
                formik.setSubmitting(false);
            });
    }

    const callExternalChallenge = (scheme, returnUrl) => {
        // Need to encode the returnUrl for the querystring
        let encodedReturnUrl = encodeURIComponent(getUrl(returnUrl));
        window.location = `${process.env.REACT_APP_UCAUTH_BASE_PROTO}${process.env.REACT_APP_UCAUTH_BASE_HOST}/identity/externalAccount/challenge?scheme=${scheme}&returnUrl=${encodedReturnUrl}`;
    }

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event) => {
        event.preventDefault();
    };

    return (
        <>
            <Box
                sx={{
                    marginTop: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <form onSubmit={formik.handleSubmit}
                >
                    {externalSuccessMessage && !showFormError && <Alert severity="success">{externalSuccessMessage}</Alert>}
                    {externalErrorMessage && !showFormError && <Alert severity="error">{externalErrorMessage}</Alert>}
                    {showFormError && (
                    <>
                        <Alert sx={{ mt: 2 }} severity="error">{errorMessage}</Alert>
                        { errorCode?.toUpperCase() === 'EMAILNOTCONFIRMED' && (
                            <Button
                                onClick={resendEmailConfirmation}
                                variant="contained"
                                color="warning"
                                fullWidth
                                sx={{ mt: 2, mb: 2 }}
                                disabled={formik.isSubmitting}
                                >Resend email confirmation
                            </Button>
                        )}
                    </>
                    )}
                    {(externalLoginProviders.isLoading || externalLoginProviders.isError) &&
                        <Box sx={{ mt: 1, textAlign: 'center' }}>
                            <CircularProgress color="primary" size={50} aria-label="Loading..." title="Loading..." />
                        </Box>
                    }
                    {!externalLoginProviders.isLoading && externalLoginProviders.result?.find(provider => provider.authenticationScheme === "DefaultPassword") &&
                    <>
                    <TextField sx={{ mt: 2 }}
                        type="email"
                        name="email"
                        label="Email Address"
                        data-cy-id="signIn-form-email"
                        fullWidth
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                    />
                    <FormControl sx={{ mt: 2 }} variant="outlined" fullWidth>
                        <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            data-cy-id="signIn-form-password"
                            fullWidth
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showPassword ? 'hide the password' : 'display the password'
                                        }
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        onMouseUp={handleMouseUpPassword}
                                        edge="end"
                                        sx={{ color: theme.palette.primary.main }}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="Password"
                        />
                    </FormControl>
                    <FormControlLabel
                        name="rememberMe"
                        value={formik.values.rememberMe}
                        onChange={formik.handleChange}
                        control={<Checkbox value="rememberMe" color="primary" />}
                        label="Remember me"
                        data-cy-id="signIn-form-remember"
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        data-cy-id="signIn-form-submit"
                        disabled={formik.isSubmitting}
                        sx={{ mt: 1, mb: 0 }}
                        >
                            <LoginIcon sx={{ mr: 1 }} />
                            Sign In
                    </Button>
                    </>
                    }
                    {!externalLoginProviders.isLoading && externalLoginProviders.result?.length > 0 &&
                        <>
                        {!externalLoginProviders.isLoading &&
                            externalLoginProviders.result?.find(provider => provider.authenticationScheme === "DefaultPassword") &&
                            externalLoginProviders.result?.find(provider => provider.authenticationScheme !== "DefaultPassword") &&
                                <Divider style={{paddingTop: "16px"}}>OR</Divider>
                            }
                        {externalLoginProviders.result?.map((externalProvider) => (
                            <>
                                { externalProvider.authenticationScheme !== "DefaultPassword" &&
                                <Button
                                    key={externalProvider.authenticationScheme}
                                    variant="outlined"
                                    startIcon={<ProviderIcon provider={externalProvider} />}
                                    fullWidth
                                    onClick={() => callExternalChallenge(externalProvider.authenticationScheme, returnUrlParam)}
                                    sx={{ mt: 2 }}
                                >
                                    {externalProvider.displayName}
                                </Button>
                                }
                            </>
                            ))}
                        </>
                    }
                </form>
                {!externalLoginProviders.isLoading && externalLoginProviders.result?.find(provider => provider.authenticationScheme === "DefaultPassword") &&
                <Grid container sx={{ mt: 1 }}>
                    <Grid item xs>
                            <Link
                                href={getUrl("/account/forgotPassword?ReturnUrl=" + encodeURIComponent(returnUrlParam))} variant="body2"
                                sx={{ opacity: .6 }}
                            >
                            Forgot password?
                        </Link>
                    </Grid>
                    {!loginContext.isLoading && !loginContext.result?.clientOnlyUserRegistration && (
                        <Grid item>
                                <Link
                                    href={getUrl("/account/register?ReturnUrl=" + encodeURIComponent(returnUrlParam))} variant="body2"
                                    sx={{ opacity: .6 }}
                                >
                                {"Don't have an account? Sign Up"}
                            </Link>
                        </Grid>
                    )}
                </Grid>
                }
            </Box>
        </>
    );
}